import { cloneDeep, max, min } from 'lodash';
import { useRef } from 'react';
import { getFramework, trackConfig, useIdentity } from '@/framework';
import { ITouchKitEvent } from '@/utils/event-kit';
import { convertLayerPointToOrigin, getCenterPointForOrigin, getNewCenterPoint } from '@/utils/layer-kit';
import {
    addPoint,
    getActualAngle,
    getPointsBetweenAngle,
    rotatePointWithFocus,
    translatePointAngle,
} from '@/utils/math-kit';
import {
    BaseLayerViewType,
    DirectionType,
    ILayerProps,
    IMarkupLayerProps,
    IPoint,
    LayerType,
    MarkupTextPositionType,
    ResizeOperateType,
    ScaleOperateType,
} from '@/version-types';
import { LabelKit, MARKUP_POINT_SIZE, updateScreenshotUrl } from '../utils';

/**
 * 单点控制和调整大小
 *
 * @param newLayer - 新的图层属性
 * @param originLayer - 原始图层属性
 * @param startPoint - 起始坐标点
 * @param currentPoint - 当前坐标点
 * @param operateType - 操作类型
 * @param scale - 缩放比例
 */
function adjustResizeMarkupSize(
    newLayer: ILayerProps,
    originLayer: ILayerProps,
    startPoint: IPoint,
    currentPoint: IPoint,
    operateType: ScaleOperateType | ResizeOperateType,
    scale: number,
) {
    const isTop =
        operateType === ResizeOperateType.TC ||
        operateType === ScaleOperateType.LT ||
        operateType === ScaleOperateType.RT;
    const direction = isTop ? DirectionType.OPPOSITE : DirectionType.POSITIVE;
    startPoint = translatePointAngle(startPoint, -originLayer.rotate);
    currentPoint = translatePointAngle(currentPoint, -originLayer.rotate);
    const top = currentPoint.y - startPoint.y;
    const offset =
        Math.floor((top / scale) * direction) * ((newLayer.data as IMarkupLayerProps).imageTransform.flipV ? -1 : 1);

    const newLayerData = newLayer.data as IMarkupLayerProps;
    const originData = originLayer.data as IMarkupLayerProps;

    const index = isTop ? 0 : 1;
    const anchorTypes =
        operateType === ResizeOperateType.TC || operateType === ResizeOperateType.BC
            ? ['arrowLeft', 'arrowRight']
            : operateType === ScaleOperateType.LT || operateType === ScaleOperateType.LB
              ? ['arrowLeft']
              : ['arrowRight'];
    const {
        renderArea: { up, down },
    } = LabelKit.getLabelRenderInfo(newLayer.width, newLayerData.fill.width, newLayer);

    const leftPointHeight = originData.startPoint
        ? (originData.startPoint.height / MARKUP_POINT_SIZE) * originData.fill.width
        : 0;
    const rightPointHeight = originData.endPoint
        ? (originData.endPoint.height / MARKUP_POINT_SIZE) * originData.fill.width
        : 0;
    const halfPointHeight = Math.ceil(Math.max(leftPointHeight / 2, rightPointHeight / 2));

    if (isTop) {
        for (let i = 0; i < anchorTypes.length; i++) {
            const anchorType = anchorTypes[i];
            newLayerData.anchor[anchorType][index] = Math.max(originData.anchor[anchorType][index] + offset, 0);
        }
        newLayerData.centerOffset.y = Math.max(
            newLayerData.anchor.arrowLeft[0],
            newLayerData.anchor.arrowRight[0],
            halfPointHeight,
            up,
        );
    } else {
        for (let i = 0; i < anchorTypes.length; i++) {
            const anchorType = anchorTypes[i];
            newLayerData.anchor[anchorType][index] = Math.min(originData.anchor[anchorType][index] - offset, 0);
        }
    }
    const topMinHeight = Math.max(
        newLayerData.anchor.arrowLeft[0],
        newLayerData.anchor.arrowRight[0],
        halfPointHeight,
        up,
    );
    const bottomMinHeight = Math.min(
        newLayerData.anchor.arrowLeft[1],
        newLayerData.anchor.arrowRight[1],
        -halfPointHeight,
        down,
    );
    newLayer.height = topMinHeight - bottomMinHeight;
    const newCenter = getNewCenterPoint(originLayer as any, newLayer, isTop, 1);
    newLayer.left = newCenter.x - newLayer.width / 2;
    newLayer.top = newCenter.y - newLayer.height / 2;

    return newLayer;
}

export const useArrowOperate = () => {
    const identity = useIdentity();
    const oldInfo = useRef<ILayerProps>();
    const leftStartPoint = useRef<IPoint>();
    const rightStartPoint = useRef<IPoint>();
    const originCenterPoint = useRef<IPoint>();
    const mouseStartPoint = useRef<IPoint>();
    const labelKit = useRef<LabelKit>();
    const framework = getFramework(identity);

    return {
        /** 控制描点，调整高 */
        resize(e: ITouchKitEvent, layer: ILayerProps, operateType: ScaleOperateType) {
            if (e.isStart) {
                framework.operate.setIsBaseOperate(true);
                oldInfo.current = cloneDeep(layer);
            }

            if (!oldInfo.current) return;
            const scale = framework.stage.getScale();

            const newLayer = adjustResizeMarkupSize(
                cloneDeep(layer),
                oldInfo.current,
                e.startPoint,
                e.currentPoint,
                operateType,
                scale,
            );

            framework.stage.addOrUpdate(newLayer);
            framework.operate.cursorTip.set({
                type: 'resize',
                x: e.x,
                y: e.y,
                width: newLayer.width,
                height: newLayer.height,
                isMin: false,
                rotate: newLayer.rotate,
            });

            if (e.isEnd) {
                framework.operate.cursorTip.clear();
                framework.history.write(oldInfo.current, trackConfig[LayerType.MARKUP_LAYER].rotate);
                framework.events.emitBaseOperate(
                    layer.id,
                    BaseLayerViewType.CUSTOM,
                    {
                        isStart: false,
                        isEnd: true,
                    },
                    {
                        data: { newLayer, oldLayer: oldInfo.current },
                    },
                );
                framework.operate.setIsBaseOperate(false);
                framework.operate.setActiveLayer(layer.id);
                updateScreenshotUrl(identity, layer.id);
            }
        },
        /** 控制中心点，调整宽，角度 */
        transform(e: ITouchKitEvent, layer: ILayerProps, operateType: ResizeOperateType) {
            const scale = framework.stage.getScale();
            if (e.isStart) {
                framework.operate.setIsBaseOperate(true);
                labelKit.current = new LabelKit();
                const data = layer.data as IMarkupLayerProps;
                oldInfo.current = cloneDeep(layer);
                const centerPoint = getCenterPointForOrigin(layer);
                originCenterPoint.current = centerPoint;
                rightStartPoint.current = rotatePointWithFocus(
                    { x: layer.left + layer.width, y: data.centerOffset.y + layer.top },
                    centerPoint,
                    layer.rotate,
                );
                leftStartPoint.current = rotatePointWithFocus(
                    { x: layer.left, y: data.centerOffset.y + layer.top },
                    centerPoint,
                    layer.rotate,
                );
                mouseStartPoint.current = convertLayerPointToOrigin(e.currentPoint, scale);
            }

            if (
                !rightStartPoint.current ||
                !originCenterPoint.current ||
                !leftStartPoint.current ||
                !mouseStartPoint.current ||
                !oldInfo.current
            )
                return;

            const mouseCurPoint = convertLayerPointToOrigin(e.currentPoint, scale);
            const offsetPoint = {
                x: ~~(mouseCurPoint.x - mouseStartPoint.current.x),
                y: ~~(mouseCurPoint.y - mouseStartPoint.current.y),
            };

            const leftCurPoint = addPoint(offsetPoint, leftStartPoint.current);
            const rightCurPoint = addPoint(offsetPoint, rightStartPoint.current);

            const startPoint = operateType === ResizeOperateType.RC ? leftStartPoint.current : rightStartPoint.current;
            const endPoint = operateType === ResizeOperateType.RC ? rightCurPoint : leftCurPoint;
            const originEndPoint =
                operateType === ResizeOperateType.RC ? rightStartPoint.current : leftStartPoint.current;

            const { x: x1, y: y1 } = startPoint;
            const { x: x2, y: y2 } = endPoint;
            const maxX = max([x1, x2]) as number;
            const minX = min([x1, x2]) as number;
            const maxY = max([y1, y2]) as number;
            const minY = min([y1, y2]) as number;
            const line = Math.pow(maxY - minY, 2) + Math.pow(maxX - minX, 2);
            const rotate = -getPointsBetweenAngle(
                {
                    x: x2 - x1,
                    y: y2 - y1,
                },
                { x: operateType === ResizeOperateType.RC ? 1 : -1, y: 0 },
            );
            const angle = getPointsBetweenAngle(
                {
                    x: originEndPoint.x - startPoint.x,
                    y: originEndPoint.y - startPoint.y,
                },
                {
                    x: endPoint.x - startPoint.x,
                    y: endPoint.y - startPoint.y,
                },
            );

            const focusPoint = startPoint; //operateType === ResizeOperateType.LC ? endPoint : startPoint;
            const centerPoint = rotatePointWithFocus(originCenterPoint.current, focusPoint, angle);
            // const originPointHtml = `<div style="position:absolute;top:${originCenterPoint.current.y}px;left:${originCenterPoint.current.x}px;height:4px;width:4px;background:rgb(255,255,0);"></div>`;
            // const curPointHtml = `<div style="position:absolute;top:${centerPoint.y}px;left:${centerPoint.x}px;height:4px;width:4px;background:blue;"></div>`;
            // if (document.getElementById('test11'))
            //     document.getElementById('test11')!.innerHTML = `${originPointHtml}${curPointHtml}`;

            const newLayer = cloneDeep(oldInfo.current);
            const labelPosition = (newLayer.data as IMarkupLayerProps).label.position;
            const newTop = centerPoint.y - newLayer.height / 2;
            const newLeft = centerPoint.x - newLayer.width / 2;
            const newWidth = +Math.sqrt(line).toFixed(0);
            const newHeight = newLayer.height;
            const newCenter = getNewCenterPoint(
                {
                    rotate,
                    width: newLayer.width,
                    height: newLayer.height,
                    left: newLeft,
                    top: newTop,
                },
                {
                    width: newWidth,
                    height: newHeight,
                },
                false,
                operateType === ResizeOperateType.LC ? -1 : 1,
            );

            if (labelKit.current) {
                const data = newLayer.data as IMarkupLayerProps;
                const res = labelKit.current.getFontSize(newLayer, data.label.fontSize, newWidth);
                data.label.fontSize = res.fontSize;
                data.label.width = res.width;
                data.label.height = res.height;
            }

            // 旋转角度+宽度
            newLayer.rotate = getActualAngle(rotate);
            newLayer.width = newWidth;
            newLayer.left = newCenter.x - newWidth / 2;
            newLayer.top = newCenter.y - newHeight / 2;

            // 宽度变更，间距会同步变更，需要修正高度
            if (labelPosition !== MarkupTextPositionType.Center) {
                const {
                    renderArea: { up: oldRenderAreaUp, down: oldRenderAreaDown },
                } = LabelKit.getLabelRenderInfo(
                    oldInfo.current.width,
                    (oldInfo.current.data as IMarkupLayerProps).fill.width,
                    oldInfo.current,
                );
                const {
                    renderArea: { up: renderAreaUp, down: renderAreaDown },
                } = LabelKit.getLabelRenderInfo(
                    newLayer.width,
                    (newLayer.data as IMarkupLayerProps).fill.width,
                    newLayer,
                );

                const { anchor } = newLayer.data as IMarkupLayerProps;
                const oldInfoData = oldInfo.current.data as IMarkupLayerProps;

                const leftPointHeight = oldInfoData.startPoint
                    ? (oldInfoData.startPoint.height / MARKUP_POINT_SIZE) * oldInfoData.fill.width
                    : 0;
                const rightPointHeight = oldInfoData.endPoint
                    ? (oldInfoData.endPoint.height / MARKUP_POINT_SIZE) * oldInfoData.fill.width
                    : 0;
                const halfPointHeight = Math.ceil(Math.max(leftPointHeight / 2, rightPointHeight / 2));

                const { anchor: oldAnchor } = oldInfoData;
                const upOrigin = Math.max(
                    oldAnchor.arrowLeft[0],
                    oldAnchor.arrowRight[0],
                    oldRenderAreaUp,
                    halfPointHeight,
                );
                const upResult = Math.max(anchor.arrowLeft[0], anchor.arrowRight[0], renderAreaUp, halfPointHeight);
                const downOrigin = Math.min(
                    anchor.arrowLeft[1],
                    anchor.arrowRight[1],
                    oldRenderAreaDown,
                    -halfPointHeight,
                );
                const downResult = Math.min(
                    anchor.arrowLeft[1],
                    anchor.arrowRight[1],
                    renderAreaDown,
                    -halfPointHeight,
                );
                const centerOffset = (newLayer.data as IMarkupLayerProps).centerOffset;

                if (labelPosition === MarkupTextPositionType.Up) {
                    centerOffset.y = layer.height + downResult;
                } else {
                    centerOffset.y = upResult;
                }

                function fixPosition(finalHeight: number, direction: number) {
                    getNewCenterPoint(
                        {
                            rotate,
                            width: newLayer.width,
                            height: newLayer.height,
                            left: newLayer.left,
                            top: newLayer.top,
                        },
                        {
                            width: newLayer.width,
                            height: finalHeight,
                        },
                        false,
                        1,
                    );
                    newLayer.height = finalHeight;
                    newLayer.left = newCenter.x - newLayer.width / 2;
                    newLayer.top = newCenter.y - finalHeight / 2;
                }
                if (upResult !== upOrigin) {
                    const finalHeight = newLayer.height + (upResult - upOrigin);
                    fixPosition(finalHeight, 1);
                }
                if (downResult !== downOrigin) {
                    const finalHeight = newLayer.height - (downResult - downOrigin);
                    fixPosition(finalHeight, -1);
                }
            }

            // 智能 45 90 度吸附
            const displayRotate = newLayer.rotate;
            const quotient = Math.round(displayRotate / 45);
            const remainder = displayRotate % 45;
            if (Math.abs(remainder) > 43 || Math.abs(remainder) < 2) {
                const fixAngle = quotient * 45 - displayRotate;
                const originCenter = getCenterPointForOrigin(newLayer);
                const newCenter = rotatePointWithFocus(originCenter, startPoint, fixAngle);
                const distRotate = ~~(displayRotate + fixAngle);
                newLayer.rotate = distRotate;
                newLayer.top = newCenter.y - newLayer.height / 2;
                newLayer.left = newCenter.x - newLayer.width / 2;
            }

            framework.stage.addOrUpdate(newLayer);
            framework.operate.cursorTip.set({
                type: 'resize',
                x: e.x,
                y: e.y,
                width: newLayer.width,
                height: newLayer.height,
                isMin: false,
                rotate: newLayer.rotate,
            });
            if (e.isEnd) {
                framework.operate.cursorTip.clear();
                labelKit.current?.destroy();
                framework.history.write(oldInfo.current, trackConfig[LayerType.MARKUP_LAYER].rotate);
                framework.operate.setIsBaseOperate(false);
                framework.events.emitBaseOperate(
                    layer.id,
                    BaseLayerViewType.CUSTOM,
                    {
                        isStart: false,
                        isEnd: true,
                    },
                    {
                        data: {
                            newLayer: newLayer,
                            oldLayer: oldInfo.current,
                        },
                    },
                );
                framework.operate.setActiveLayer(layer.id);
                updateScreenshotUrl(identity, layer.id);
            }
        },
    };
};
